import { APIClient, ChatStreamClient, IChatStreamParams, IChatStreamEvent } from '@innerhive-internal/innerhive-api-client';
import { ILink } from '../caremap/WithLinks';
import { ICareMapKey } from '../caremap/ICareMap';
import { IOrganization } from '../organizations/IOrganization';
import { AppPrincipal } from '../auth/AppPrincipal';

// Re-export type for consumers
export type { IChatStreamEvent } from '@innerhive-internal/innerhive-api-client';

export interface IChatMessage {
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
}

export interface IChat {
    userId: string;
    chatId: string;
    messages: IChatMessage[];
    createdAt: string;
    lastMessageAt: string;
    // Written by the api after the first exchange; absent on chats from before titles.
    title?: string;
    // A caseload chat: the organization it was asked of and the client it was opened on.
    orgId?: string;
    activeCareMapKey?: ICareMapKey;
    links: {
        self: ILink;
    };
}

export class BeeChatClient {
    static inject = () => [APIClient, ChatStreamClient, AppPrincipal];

    constructor(
        private apiClient: APIClient,
        private chatStreamClient: ChatStreamClient,
        private principal: AppPrincipal
    ) {}

    getChats = async (): Promise<IChat[]> => {
        const entry = await this.apiClient.entry();
        if (!entry?.links?.chats) {
            return [];
        }
        const chats = await this.apiClient.get(entry.links.chats);
        chats.sort((a: IChat, b: IChat) => new Date(b.lastMessageAt).valueOf() - new Date(a.lastMessageAt).valueOf());
        return chats;
    };

    /** A member of staff's chats with this organization's caseload: the organization's own link, templated on the member. */
    getOrganizationChats = async (params: { organization: IOrganization }): Promise<IChat[]> => {
        const { organization } = params;
        if (!organization.links.chats) {
            return [];
        }
        const chats: IChat[] = await this.apiClient.get(organization.links.chats, undefined, { userId: this.principal.userId });
        chats.sort((a, b) => new Date(b.lastMessageAt).valueOf() - new Date(a.lastMessageAt).valueOf());
        return chats;
    };

    streamChat = (params: IChatStreamParams): AsyncGenerator<IChatStreamEvent> => {
        return this.chatStreamClient.streamChat(params);
    };

    extractChat = async (params: { link: ILink; chatId: string; careMapKey?: ICareMapKey }): Promise<{ extracted: number }> => {
        const { link, chatId, careMapKey } = params;
        // apiClient.post substitutes [[chatId]] in the link href via the params arg, so we
        // pass the link object directly rather than constructing the URL client-side.
        return await this.apiClient.post(link, { careMapKey }, undefined, { chatId });
    };
}
